import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Database, ExternalLink, RefreshCw, Search, ShieldAlert } from "lucide-react";
import { getMapeamentoOrigemDados } from "../../api";
import SideBarNavOrigemDados from "../../componets/SideBarNavOrigemDados";

const origemColors = {
  "Google Sheets": "bg-emerald-100 text-emerald-700",
  "Banco de Dados": "bg-blue-100 text-blue-700",
  "API Externa": "bg-amber-100 text-amber-700",
};

function OrigemBadge({ origem }) {
  return (
    <span
      className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
        origemColors[origem] || "bg-gray-100 text-gray-600"
      }`}
    >
      {origem}
    </span>
  );
}

export default function MapeamentoOrigemDados() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState("");

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const res = await getMapeamentoOrigemDados();
      setDados(res);
    } catch (e) {
      setErro(e.message || "Acesso não autorizado.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  const itens = useMemo(() => dados?.itens ?? [], [dados]);

  const itensFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return itens;
    return itens.filter((item) =>
      Object.values(item).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [itens, filtro]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    for (const item of itensFiltrados) {
      const chave = item.modulo || "Outros";
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave).push(item);
    }
    return Array.from(mapa.entries());
  }, [itensFiltrados]);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavOrigemDados isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-1.5 rounded-lg hover:bg-white/10 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-lg font-bold tracking-tight">
            Painel Admin · Mapeamento de Origem dos Dados
          </span>
        </div>
        <button
          onClick={() => navigate("/inicio")}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
          }}
        >
          Menu Principal
        </button>
      </header>

      <main className="flex-1 px-6 py-8 max-w-6xl mx-auto w-full">
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 mb-6 text-sm">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <p>
            Conteúdo restrito a administradores. Os links abaixo abrem as planilhas de origem
            diretamente no Google Sheets — não compartilhe fora da equipe financeira.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-gray-800">
                {dados?.titulo || "Mapeamento de Origem dos Dados"}
              </h2>
              {dados?.descricao && (
                <p className="text-xs text-gray-500 mt-0.5">{dados.descricao}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Buscar módulo ou planilha…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-pink-400 w-72"
                />
              </div>
              <button
                onClick={carregar}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-pink-600 transition"
              >
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </button>
            </div>
          </div>

          {loading && (
            <div className="py-16 text-center text-gray-400 text-sm">Carregando dados…</div>
          )}

          {!loading && erro && (
            <div className="py-16 text-center text-red-500 text-sm">{erro}</div>
          )}

          {!loading && !erro && grupos.length === 0 && (
            <div className="py-16 text-center text-gray-400 text-sm">Nenhum resultado encontrado.</div>
          )}

          {!loading && !erro && grupos.length > 0 && (
            <div className="divide-y divide-gray-100">
              {grupos.map(([modulo, linhas]) => (
                <div key={modulo} className="px-6 py-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Database className="w-4 h-4 text-purple-700" />
                    <h3 className="text-sm font-semibold text-gray-800">{modulo}</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-400 uppercase tracking-wide">
                          <th className="py-2 pr-4 font-medium">Origem</th>
                          <th className="py-2 pr-4 font-medium">Planilha / Aba</th>
                          <th className="py-2 pr-4 font-medium">Observações</th>
                          <th className="py-2 font-medium text-right">Acesso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((item, idx) => (
                          <tr key={`${modulo}-${idx}`} className="border-t border-gray-50 align-top">
                            <td className="py-2.5 pr-4">
                              <OrigemBadge origem={item.origem_dados} />
                            </td>
                            <td className="py-2.5 pr-4 text-gray-600">
                              {item.planilha_google_sheets && item.planilha_google_sheets !== "—" ? (
                                <>
                                  <div>{item.planilha_google_sheets}</div>
                                  {item.aba_worksheet && item.aba_worksheet !== "—" && (
                                    <div className="text-xs text-gray-400">{item.aba_worksheet}</div>
                                  )}
                                </>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="py-2.5 pr-4 text-gray-500 max-w-md">{item.observacoes}</td>
                            <td className="py-2.5 text-right">
                              {item.link_planilha ? (
                                <a
                                  href={item.link_planilha}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg hover:bg-emerald-100 transition whitespace-nowrap"
                                >
                                  Abrir planilha
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="text-center text-xs text-gray-400 py-6">
        © {new Date().getFullYear()} Youx Group · by financeiro
      </footer>
      </div>
    </div>
  );
}
